import { Dispatch } from "redux";
import api from "./api";
import { TourDetails, Gallery } from "@/data/types";

// ✅ Define action types
const GET_TOUR = "[TOUR] GET TOUR";
const GET_TOUR_FAILURE = "[TOUR] GET TOUR FAILURE";
const GET_TOUR_GALLERY = "[TOUR] GET TOUR GALLERY";
const GET_TOUR_GALLERY_FAILURE = "[TOUR] GET TOUR GALLERY FAILURE";

// ✅ Define action interfaces
interface GetTourAction {
  type: typeof GET_TOUR;
  payload: TourDetails;
}

interface GetTourFailureAction {
  type: typeof GET_TOUR_FAILURE;
  payload: string; // Error message
}

interface GetTourGalleryAction {
  type: typeof GET_TOUR_GALLERY;
  payload: Gallery[];
}

interface GetTourGalleryFailureAction {
  type: typeof GET_TOUR_GALLERY_FAILURE;
  payload: string;
}

type TourActionTypes =
  | GetTourAction
  | GetTourFailureAction
  | GetTourGalleryAction
  | GetTourGalleryFailureAction;

// ✅ Sync setters (for server-side hydration)
export const setTourDetails = (payload: TourDetails) => ({
  type: GET_TOUR as typeof GET_TOUR,
  payload,
});

export const setGalleries = (payload: Gallery[]) => ({
  type: GET_TOUR_GALLERY as typeof GET_TOUR_GALLERY,
  payload,
});

// ✅ Async action: Fetch tour details
export const getTourDetails =
  (slug: string, language: string) =>
  async (dispatch: Dispatch<TourActionTypes>) => {
    try {
      // `api` joint le jeton s'il y en a un : un tour reserve a un client
      // (GT-261007-HVGQ) ne s'ouvre qu'a lui. Sans jeton, meme reponse qu'avant.
      const response = await api.get(`/api/tour/${slug}`, {
        params: { language },
      });
      if (response.data) {
        dispatch({
          type: GET_TOUR,
          payload: response.data,
        });
      } else {
        dispatch({
          type: GET_TOUR_FAILURE,
          payload: "No tour found.",
        });
      }
    } catch (error: any) {
      console.error("Error fetching tour details:", error);
      dispatch({
        type: GET_TOUR_FAILURE,
        payload: error.message || "Error fetching tour details",
      });
    }
  };

// ✅ Async action: Fetch tour gallery
export const getTourGallery =
  (slug: string) => async (dispatch: Dispatch<TourActionTypes>) => {
    try {
      const response = await api.get(`/api/tour/${slug}/get-gallery`);

      if (response.data && response.data.galleries) {
        dispatch({
          type: GET_TOUR_GALLERY,
          payload: response.data.galleries, // ✅ Store gallery array in Redux
        });
      } else {
        dispatch({
          type: GET_TOUR_GALLERY_FAILURE,
          payload: "No galleries found.",
        });
      }
    } catch (error: any) {
      console.error("Error fetching tour gallery:", error);
      dispatch({
        type: GET_TOUR_GALLERY_FAILURE,
        payload: error.message || "Error fetching tour gallery",
      });
    }
  };

// ✅ Initial Redux state
const initialState = {
  tourDetails: null as TourDetails | null,
  error: null as string | null,
  galleries: [] as Gallery[], // ✅ Store galleries separately
};

// ✅ Reducer function
const tourReducer = (state = initialState, action: TourActionTypes) => {
  switch (action.type) {
    case GET_TOUR:
      return {
        ...state,
        tourDetails: action.payload,
        error: null,
      };
    case GET_TOUR_FAILURE:
      return {
        ...state,
        error: action.payload,
      };
    case GET_TOUR_GALLERY:
      return {
        ...state,
        galleries: action.payload, // ✅ Store gallery data in Redux
        error: null,
      };
    case GET_TOUR_GALLERY_FAILURE:
      return {
        ...state,
        galleries: [],
        error: action.payload,
      };
    default:
      return state;
  }
};

export default tourReducer;
